<template>
  <div class="mpp">

    <!-- Hero -->
    <header class="mpp__hero">
      <div class="mpp__hero-bg"></div>
      <div class="mpp__hero-overlay"></div>
      <div class="mpp__hero-glow"></div>

      <div class="mpp__hero-inner">
        <div class="mpp__hero-badge">
          <span class="mpp__hero-badge-dot">
            <span class="mpp__hero-badge-ping"></span>
          </span>
          {{ $t('blog.masterpartner.badge') }}
        </div>

        <h1 class="mpp__hero-title">
          {{ $t('blog.masterpartner.heroTitle1') }}<br>
          <span class="text-gold">{{ $t('blog.masterpartner.heroTitle2') }}</span>
        </h1>
        <p class="mpp__hero-subline">{{ $t('blog.masterpartner.heroSubline') }}</p>
        <p class="mpp__hero-lead">{{ $t('blog.masterpartner.heroLead') }}</p>

        <div class="mpp__hero-deadline">
          <i class="fas fa-coins"></i>
          {{ $t('blog.masterpartner.heroDeadline') }} <strong>{{ $t('blog.masterpartner.heroDeadlinePeriod') }}</strong>
        </div>

        <div class="mpp__hero-actions">
          <a
            href="#mpp-contact"
            class="mpp__hero-btn mpp__hero-btn--primary"
          >
            <i class="fas fa-paper-plane"></i>
            {{ $t('blog.masterpartner.openBtn') }}
          </a>
          <a href="#benefits" class="mpp__hero-btn mpp__hero-btn--ghost">
            <i class="fas fa-arrow-down"></i>
            {{ $t('blog.masterpartner.heroBtn') }}
          </a>
        </div>
      </div>

      <div class="mpp__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z" fill="#0f172a"/>
        </svg>
      </div>
    </header>

    <!-- Live Performance Showcase -->
    <section class="mpp__section mpp__section--performance">
      <div class="mpp__shell">
        <div class="mpp__section-header">
          <div class="mpp__section-eyebrow">Real Performance</div>
          <h2 class="mpp__section-title">{{ $t('blog.masterpartner.perfTitle') }}</h2>
          <p class="mpp__section-sub">{{ $t('blog.masterpartner.perfSub') }}</p>
          <div class="mpp__divider"></div>
        </div>

        <div class="mpp__perf-dashboard">
          <div class="mpp__perf-card">
            <div class="mpp__perf-header">
              <span class="mpp__perf-badge-live">
                <span class="mpp__perf-badge-dot"></span> LIVE DASHBOARD
              </span>
              <h3 class="mpp__perf-account-name">SHC EA PRO Core System</h3>
            </div>
            
            <div class="mpp__perf-stats">
              <div class="mpp__perf-stat-item">
                <span class="mpp__perf-stat-label">{{ $t('blog.masterpartner.perfGrowth') }}</span>
                <span class="mpp__perf-stat-val text-grow">+140.93%</span>
              </div>
              <div class="mpp__perf-stat-item">
                <span class="mpp__perf-stat-label">{{ $t('blog.masterpartner.perfMonthly') }}</span>
                <span class="mpp__perf-stat-val text-gold">30-36%</span>
              </div>
              <div class="mpp__perf-stat-item">
                <span class="mpp__perf-stat-label">{{ $t('blog.masterpartner.perfDrawdown') }}</span>
                <span class="mpp__perf-stat-val text-risk">15%</span>
              </div>
              <div class="mpp__perf-stat-item">
                <span class="mpp__perf-stat-label">{{ $t('blog.masterpartner.perfCopiers') }}</span>
                <span class="mpp__perf-stat-val">6,200+</span>
              </div>
            </div>

            <!-- Simulated mini chart graphic to make it look premium -->
            <div class="mpp__perf-chart-wrap">
              <svg viewBox="0 0 500 120" class="mpp__perf-svg-chart">
                <defs>
                  <linearGradient id="chart-glow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#ffd700" stop-opacity="0.3"></stop>
                    <stop offset="100%" stop-color="#ffd700" stop-opacity="0"></stop>
                  </linearGradient>
                </defs>
                <path d="M0,100 C50,90 80,70 120,75 C160,80 200,45 250,55 C300,65 340,30 380,20 C420,10 460,15 500,5 L500,120 L0,120 Z" fill="url(#chart-glow)"></path>
                <path d="M0,100 C50,90 80,70 120,75 C160,80 200,45 250,55 C300,65 340,30 380,20 C420,10 460,15 500,5" fill="none" stroke="#ffd700" stroke-width="3" stroke-linecap="round"></path>
                <circle cx="500" cy="5" r="5" fill="#ffd700" class="mpp__chart-pulse"></circle>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section id="benefits" class="mpp__section mpp__section--dark">
      <div class="mpp__shell">
        <div class="mpp__section-header">
          <div class="mpp__section-eyebrow">Master Partner Benefits</div>
          <h2 class="mpp__section-title">{{ $t('blog.masterpartner.benefitTitle') }}</h2>
          <div class="mpp__divider"></div>
        </div>

        <div class="mpp__benefits">
          <div class="mpp__benefit-card">
            <div class="mpp__benefit-icon">
              <i class="fas fa-percentage"></i>
            </div>
            <h3 class="mpp__benefit-card-title">
              {{ $t('blog.masterpartner.benefit1Title') }}
              <span class="text-sub">{{ $t('blog.masterpartner.benefit1Sub') }}</span>
            </h3>
            <p class="mpp__benefit-card-desc">{{ $t('blog.masterpartner.benefit1Desc') }}</p>
          </div>

          <div class="mpp__benefit-card">
            <div class="mpp__benefit-icon">
              <i class="fas fa-sliders-h"></i>
            </div>
            <h3 class="mpp__benefit-card-title">
              {{ $t('blog.masterpartner.benefit2Title') }}
              <span class="text-sub">{{ $t('blog.masterpartner.benefit2Sub') }}</span>
            </h3>
            <p class="mpp__benefit-card-desc">{{ $t('blog.masterpartner.benefit2Desc') }}</p>
          </div>

          <div class="mpp__benefit-card">
            <div class="mpp__benefit-icon">
              <i class="fas fa-hand-holding-usd"></i>
            </div>
            <h3 class="mpp__benefit-card-title">
              {{ $t('blog.masterpartner.benefit3Title') }}
              <span class="text-sub">{{ $t('blog.masterpartner.benefit3Sub') }}</span>
            </h3>
            <p class="mpp__benefit-card-desc">{{ $t('blog.masterpartner.benefit3Desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Calculator Section (Interactive Element) -->
    <section class="mpp__section mpp__section--calculator">
      <div class="mpp__shell">
        <div class="mpp__section-header">
          <div class="mpp__section-eyebrow">Earning Estimator</div>
          <h2 class="mpp__section-title">{{ $t('blog.masterpartner.calculatorTitle') }}</h2>
          <p class="mpp__section-sub">{{ $t('blog.masterpartner.calculatorDesc') }}</p>
          <div class="mpp__divider"></div>
        </div>

        <div class="mpp__calc-container">
          <div class="mpp__calc-sliders">
            <!-- Slider 1: Capital -->
            <div class="mpp__calc-field">
              <label for="capital-range">
                <span>{{ $t('blog.masterpartner.calcCapital') }}</span>
                <span class="mpp__calc-value">${{ formatNumber(clientCapital) }}</span>
              </label>
              <input
                id="capital-range"
                v-model.number="clientCapital"
                type="range"
                min="100000"
                max="5000000"
                step="50000"
                class="mpp__range"
              />
              <div class="mpp__range-labels">
                <span>$100k</span>
                <span>$1M</span>
                <span>$3M</span>
                <span>$5M</span>
              </div>
            </div>

            <!-- Slider 3: Profit Share Rate -->
            <div class="mpp__calc-field">
              <label for="rate-range">
                <span>{{ $t('blog.masterpartner.calcProfitShare') }}</span>
                <span class="mpp__calc-value">{{ profitShareRate }}%</span>
              </label>
              <input
                id="rate-range"
                v-model.number="profitShareRate"
                type="range"
                min="10"
                max="50"
                step="5"
                class="mpp__range"
              />
              <div class="mpp__range-labels">
                <span>10%</span>
                <span>20%</span>
                <span>30%</span>
                <span>40%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          <div class="mpp__calc-results">
            <h3 class="mpp__results-title">{{ $t('blog.masterpartner.calcTotalRev') }}</h3>
            <div class="mpp__results-main-value">${{ formatNumber(totalMonthlyIncome) }} <span class="mpp__per-month">/mo</span></div>
            
            <!-- Partner Tier Badge -->
            <div class="mpp__tier-badge" :class="partnerTier.class">
              <div class="mpp__tier-header">
                <i :class="partnerTier.icon"></i>
                <span class="mpp__tier-name">{{ partnerTier.name }}</span>
              </div>
              <p class="mpp__tier-quote">"{{ partnerTier.quote }}"</p>
            </div>
            
            <div class="mpp__results-breakdown">
              <div class="mpp__breakdown-item">
                <span>{{ $t('blog.masterpartner.calcEstProfit') }}</span>
                <strong>+${{ formatNumber(estProfitShare) }}</strong>
              </div>
              <div class="mpp__breakdown-item">
                <span>{{ $t('blog.masterpartner.calcEstCommission') }}</span>
                <strong>+${{ formatNumber(estBackCommission) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Requirements & Parameters Section -->
    <section class="mpp__section mpp__section--dark-alt">
      <div class="mpp__shell">
        <div class="mpp__section-header">
          <div class="mpp__section-eyebrow">Program Rules</div>
          <h2 class="mpp__section-title">{{ $t('blog.masterpartner.reqTitle') }}</h2>
          <div class="mpp__divider"></div>
        </div>

        <div class="mpp__reqs">
          <div class="mpp__req-item">
            <div class="mpp__req-icon">
              <i class="fas fa-dollar-sign"></i>
            </div>
            <div class="mpp__req-body">
              <h3 class="mpp__req-item-title">{{ $t('blog.masterpartner.req1Title') }}</h3>
              <p class="mpp__req-item-desc">{{ $t('blog.masterpartner.req1Desc') }}</p>
            </div>
          </div>

          <div class="mpp__req-item">
            <div class="mpp__req-icon">
              <i class="fas fa-piggy-bank"></i>
            </div>
            <div class="mpp__req-body">
              <h3 class="mpp__req-item-title">{{ $t('blog.masterpartner.req2Title') }}</h3>
              <p class="mpp__req-item-desc">{{ $t('blog.masterpartner.req2Desc') }}</p>
            </div>
          </div>

          <div class="mpp__req-item">
            <div class="mpp__req-icon">
              <i class="fas fa-chart-bar"></i>
            </div>
            <div class="mpp__req-body">
              <h3 class="mpp__req-item-title">{{ $t('blog.masterpartner.req3Title') }}</h3>
              <p class="mpp__req-item-desc">{{ $t('blog.masterpartner.req3Desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Cent Account Section -->
    <section class="mpp__section mpp__section--cent">
      <div class="mpp__shell">
        <div class="mpp__invite-box">
          <p class="mpp__invite-text">{{ $t('blog.masterpartner.inviteText') }}</p>
        </div>

        <div class="mpp__cent-card">
          <div class="mpp__cent-icon">
            <i class="fas fa-lightbulb"></i>
          </div>
          <div class="mpp__cent-content">
            <h2 class="mpp__cent-title">{{ $t('blog.masterpartner.centTitle') }}</h2>
            <p class="mpp__cent-desc">{{ $t('blog.masterpartner.centDesc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact section -->
    <section id="mpp-contact" class="mpp__section mpp__section--contact">
      <div class="mpp__shell">
        <div class="mpp__section-header">
          <h2 class="mpp__section-title">{{ $t('blog.masterpartner.contactTitle') }}</h2>
          <p class="mpp__section-sub">{{ $t('blog.masterpartner.contactSub') }}</p>
          <div class="mpp__divider"></div>
        </div>

        <div class="mpp__contact-grid">
          <!-- Contact card 1 -->
          <div class="mpp__contact-card">
            <div class="mpp__contact-card-icon">
              <i class="fab fa-telegram-plane"></i>
            </div>
            <h3 class="mpp__contact-card-title">{{ $t('blog.masterpartner.telegramLabel') }}</h3>
            <div class="mpp__contact-card-links">
              <a href="https://t.me/HT_Rim" target="_blank" class="mpp__contact-link">
                @HT_Rim <i class="fas fa-external-link-alt"></i>
              </a>
              <a href="https://t.me/songhancapital" target="_blank" class="mpp__contact-link">
                @songhancapital <i class="fas fa-external-link-alt"></i>
              </a>
            </div>
          </div>

          <!-- Contact card 2 -->
          <div class="mpp__contact-card">
            <div class="mpp__contact-card-icon">
              <i class="fas fa-phone-alt"></i>
            </div>
            <h3 class="mpp__contact-card-title">{{ $t('blog.masterpartner.phoneLabel') }}</h3>
            <div class="mpp__contact-card-value" @click="copyPhone('+84 766 726 416')">
              +84 766 726 416 (HT Rim) <i class="fas fa-copy"></i>
            </div>
          </div>
        </div>

        <div class="mpp__cta">
          <p>{{ $t('blog.masterpartner.ctaText') }}</p>
          <a href="https://t.me/HT_Rim" target="_blank" class="mpp__cta-btn">
            <i class="fab fa-telegram"></i>
            {{ $t('blog.masterpartner.ctaBtn') }}
          </a>
          <p class="mpp__disclaimer">{{ $t('blog.masterpartner.disclaimer') }}</p>
        </div>
      </div>
    </section>

    <!-- Copy toast -->
    <transition name="fade">
      <div v-if="showCopyToast" class="mpp__toast">
        <i class="fas fa-check-circle"></i> {{ $t('blog.masterpartner.toastMsg') }}
      </div>
    </transition>

  </div>
</template>

<script src="./master-partner-program.js"></script>
<style src="./master-partner-program.scss" lang="scss"></style>
